import { assign, concat, difference, filter, forEach, map, orderBy, values } from 'lodash';
import modelFactory from '../modelFactory'
import jobCostFactory from './jobCostModel';
import Specs from './specs';
import enums from '../enum';
import { jobCostSchemaVersionCheck } from 'Scripts/schemaHelper';

/* eslint-disable camelcase */

const DIFF_KEYS_EXCLUDE = ['action', 'changeOrderKey', 'createdById', 'dateCreated', 'lastModifiedById', 'dateLastModified', 'key', 'itemVersion', 'relayId'];
class ContractChildHandler {
	private _addItem;
	private _deleteItem;
	private _currentChangeOrder;
	private _modelSet;

	constructor(currentChangeOrder, adder, deleter) {
		this._addItem = adder;
		this._deleteItem = deleter;
		this._currentChangeOrder = currentChangeOrder;

		this._get_root = this._get_root.bind(this);
		this._set_vendorKey = this._set_vendorKey.bind(this);
		this._set_name = this._set_name.bind(this);
		this._set_note = this._set_note.bind(this);
	}

	get(target, name) {
		const getter = typeof name === 'string' && this[`_get_${name}`];
		if (getter) {
			return getter(target);
		}

		const result = Reflect.get(target, name);

		/**
		 * Don't love this but it is necessary to work around a bug when setting appliance and opening sizes on specs.
		 * This corresponds to jobCostEditApplianceSpecs.tsx to prevent the original size model from being mutated.
		 */

		if(typeof result?.copy === 'function' && name !== 'parent') {
			return result.copy();
		}

		return result;
	}

	set(target, name, value) {
		const setter = this[`_set_${name}`];

		if (setter) {
			setter(target, name, value);
		}
		else {
			this._setProperty(target, name, value);
		}

		return true;
	}

	_setProperty(target, name, value) {
		if (this._currentChangeOrder && this._currentChangeOrder.allowedContractEdits !== 'none') {
			if (target.changeOrderKey === this._currentChangeOrder.key) {
				Reflect.set(target, name, value);

				if (target.parent && !target.isDeletion) {
					const diffKeys = difference(target.getPropertyNames(), DIFF_KEYS_EXCLUDE);
					const patch = target.getPatch(target.parent, diffKeys);
					if (!patch.length) {
						this._deleteItem(target.key);
					}
				}
			}
			else {
				const originalVal = Reflect.get(target, name);

				// set the value on the target to allow all get and set filters to run for precise comparison
				Reflect.set(target, name, value);

				const newVal = Reflect.get(target, name);

				if (originalVal !== newVal) {
					const changeItem = this._addItem(target);
					// be sure to reset the original value
					Reflect.set(target, name, originalVal);
					if (changeItem) {
						Reflect.set(changeItem, name, value);
					}
				}
			}
		}
	}

	_get_root(target) {
		return this._modelSet.get(target.rootKey || target.key);
	}

	_set_vendorKey(target, name, value) {
		if (target.getModelName() !== 'JobCostWorkArea') {
			this._setProperty(target, name, value);
		}
	}

	_set_name(target, name, value) {
		if (target.getModelName() !== 'JobCostWorkArea') {
			this._setProperty(target, name, value);
		}
	}

	_set_note(target, name, value) {
		if (target.getModelName() === 'JobCostWorkArea') {
			Reflect.set(target, name, value, target);
		}
		else {
			this._setProperty(target, name, value);
		}
	}
}

class ContractSetHandler {
	_currentChangeOrder;
	_targetSet;
	_preventAdd;
	_preventDelete;
	_setProxy;
	_addProxy;
	_deleteProxy;

	constructor(props) {
		const { targetSet, currentChangeOrder, preventAdd, preventDelete } = props;
		this._currentChangeOrder = currentChangeOrder;
		this._targetSet = targetSet;
		this._preventAdd = preventAdd;
		this._preventDelete = preventDelete;

		this._get_get = this._get_get.bind(this);
		this._get_set = this._get_set.bind(this);
		this._get_add = this._get_add.bind(this);
		this._get_delete = this._get_delete.bind(this);
	}

	get(target, name) {
		const getter = typeof name === 'string' && this[`_get_${name}`];
		if (getter) {
			return getter(target, this);
		}

		return Reflect.get(target, name);
	}

	_get_get(target, handler) {
		return (key) => {
			return handler.get(target, key);
		};
	}
	_get_set(target) {
		return this._getSetProxy(target);
	}
	_get_add(target) {
		return this._getAddProxy(target);
	}
	_get_delete(target) {
		return this._getDeleteProxy(target);
	}

	_getSetProxy(target) {
		this._setProxy = this._setProxy || this._setItem.bind(this, target);

		return this._setProxy;
	}

	_getAddProxy(target) {
		this._addProxy = this._addProxy || this._addItem.bind(this, target);

		return this._addProxy;
	}

	_getDeleteProxy(target) {
		this._deleteProxy = this._deleteProxy || this._deleteItem.bind(this, target);

		return this._deleteProxy;
	}

	_setItem(target, key, item) {
		this._addItem(target, item);
	}

	_addItem(target, item) {
		if (this._preventAdd) { return; }

		target._invalidated = true;
		const result = this._targetSet.add(item);

		result.changeOrderKey = this._currentChangeOrder && this._currentChangeOrder.key;

		return result;
	}

	_deleteItem(target, key) {
		if (this._preventDelete) { return; }

		const item = this._targetSet[key];

		target._invalidated = true;

		this._targetSet.delete(item.key);
	}
}

class ContractChangeTrackedSetHandler extends ContractSetHandler {

	constructor(props) {
		super(props);

		this._get_revert = this._get_revert.bind(this);
	}

	get(target, name) {
		const getter = typeof name === 'string' && this[`_get_${name}`];
		if (getter) {
			return getter(target, this);
		}

		let result = Reflect.get(target, name);

		// this is a Model child
		if (result && typeof result.getModelName === 'function') {
			result = this._getItemProxy(name, result, target);
		}

		return result;
	}

	_get_revert(target) {
		return this._getDeleteProxy(target);
	}

	_addItem(target, item, action = enums.trackableActions.Set) {
		if (this._preventAdd) { return; }

		const changeOrderKey = this._currentChangeOrder && this._currentChangeOrder.key;

		target._invalidated = true;
		let result;

		if (item.changeOrderKey !== changeOrderKey) {
			const newDoc = item.toDocument ? item.toDocument(undefined, DIFF_KEYS_EXCLUDE) : item;
			result = this._targetSet.add(newDoc);
		}
		else {
			result = this._targetSet.add(item);
		}

		result.rootKey = result.rootKey || result.key;
		result.action = action;
		result.changeOrderKey = changeOrderKey;

		return result;
	}

	_deleteItem(target, key) {
		if (this._preventDelete) { return; }

		const changeOrderKey = this._currentChangeOrder && this._currentChangeOrder.key;
		const item = this._targetSet[key];

		target._invalidated = true;

		if (item.changeOrderKey === changeOrderKey) {
			this._targetSet.delete(item.key);
		}
		else {
			this._addItem(target, item, enums.trackableActions.Unset);
		}
	}

	_getItemProxy(name, modelItem, filteredSet) {
		this[name] = this[name] || new Proxy(modelItem, new ContractChildHandler(this._currentChangeOrder, this._addItem.bind(this, filteredSet), this._deleteItem.bind(this, filteredSet)));

		return this[name];
	}
}

const CONTRACT_SETTABLE = ['accountingId', 'type'];
// const CHANGEORDER_EXCLUDED_LINEITEM_TYPES = ['delivery', 'installation', 'installation-custom', 'referral-fee'];
class ContractHandler {
	private _currentChangeOrder;
	private _strictListFilter;
	private _cabinetSpecs;
	private _innerProxy;
	private _workAreaSpecs;
	private _approvedChanges;

	constructor(currentChangeOrder, strictListFilter?) {
		this._get_currentChangeOrder = this._get_currentChangeOrder.bind(this);
		this._get_lineItems = this._get_lineItems.bind(this);
		this._get_notes = this._get_notes.bind(this);
		this._get_scheduleItems = this._get_scheduleItems.bind(this);
		this._get_specifications = this._get_specifications.bind(this);
		this._get_workAreas = this._get_workAreas.bind(this);
		this._get_paymentTerms = this._get_paymentTerms.bind(this);
		this._get_installSheets = this._get_installSheets.bind(this);
		this._get_cabinetSpecs = this._get_cabinetSpecs.bind(this);
		this._get_workAreaSpecs = this._get_workAreaSpecs.bind(this);
		this._get_allowedContractEdits = this._get_allowedContractEdits.bind(this);
		this._get_isContract = this._get_isContract.bind(this);
		this._get_withStrictFilters = this._get_withStrictFilters.bind(this);

		this._currentChangeOrder = currentChangeOrder;
		this._strictListFilter = strictListFilter;
	}

	get(target, name) {
		const getter = typeof name === 'string' && this[`_get_${name}`];
		if (getter) {
			return getter(target);
		}
		return Reflect.get(target, name);
	}

	set(target, name, value) {
		const setter = typeof name === 'string' && this[`_set_${name}`];
		if (setter) {
			setter(target, name, value);
		}
		else if (CONTRACT_SETTABLE.indexOf(name) !== -1) {
			Reflect.set(target, name, value, target);
		}

		return true;
	}
	_get_cabinetSpecs(target) {
		if (!this._cabinetSpecs) {
			this._setInnerProxy(target);
			this._cabinetSpecs = new Specs(this._innerProxy, 'cabinetSpecVersion', enums.specGroups.Cabinet, true);
		}

		return this._cabinetSpecs;
	}
	_get_workAreaSpecs(target) {
		if (!this._workAreaSpecs) {
			this._setInnerProxy(target);
			this._workAreaSpecs = new Specs(this._innerProxy, 'workAreaSpecVersion', enums.specGroups.Work_Area);
		}

		return this._workAreaSpecs;
	}

	_get_lineItems(target) {
		return this._getFilteredSet(target, 'lineItems', { changeTracked: true });
	}

	_get_notes(target) {
		return this._getFilteredSet(target, 'notes', { changeTracked: false });
	}

	_get_scheduleItems(target) {
		return this._getFilteredSet(target, 'scheduleItems', { changeTracked: true });
	}

	_get_specifications(target) {
		return this._getFilteredSet(target, 'specifications', { changeTracked: true });
	}

	_get_workAreas(target) {
		return this._getFilteredSet(target, 'workAreas', { changeTracked: true, preventDelete: true, preventAdd: true, preventStrictFilter: true });
	}

	_get_paymentTerms(target) {
		return this._getFilteredSet(target, 'paymentTerms', { changeTracked: false });
	}

	_get_installSheets(target) {
		if (jobCostSchemaVersionCheck(target, 2)) {
			return this._getFilteredSet(target, 'installSheets', { changeTracked: false });
		} else {
			return target.installSheets;
		}
	}

	_get_currentChangeOrder() {
		// get the latest of the current change order if it is attached to the job cost
		return this._currentChangeOrder;
	}

	_get_proxiedContract(target) {
		return target;
	}

	_get_allowedContractEdits(target) {
		if (this._currentChangeOrder) {
			return this._currentChangeOrder.allowedContractEdits;
		}

		return target.allowedContractEdits;
	}

	_get_isContract(target) {
		if (this._currentChangeOrder) {
			return this._currentChangeOrder.isContract;
		}

		return target.isContract;
	}

	_setInnerProxy(target) {
		this._innerProxy = this._innerProxy || new Proxy(target, new ContractHandler(this._currentChangeOrder));
	}

	_get_withStrictFilters(target) {
		return operation => {
			const initialStrict = this._strictListFilter;

			this._strictListFilter = true;
			forEach(['lineItems', 'notes', 'scheduleItems', 'specifications', 'workAreas'], k => { this[`_${k}`] = undefined; });

			// contractProxy should always be present - evaluate removing ||
			const result = operation(target.contractProxy || target);

			this._strictListFilter = initialStrict;
			forEach(['lineItems', 'notes', 'scheduleItems', 'specifications', 'workAreas'], k => { this[`_${k}`] = undefined; });

			return result;
		};
	}

	_getFilteredSet(target, setPropertyName, config) {
		let filteredSet = this[`_${setPropertyName}`];

		if (!filteredSet || filteredSet._invalidated) {
			const changeOrderKey = this._currentChangeOrder && this._currentChangeOrder.key;
			const targetSet = target[setPropertyName];

			if (config.changeTracked) {
				const handler = new ContractChangeTrackedSetHandler(assign({ targetSet: targetSet, currentChangeOrder: this._currentChangeOrder }, config));

				const approvedChanges = this._getApprovedChanges(target);
				const changesToApply = this._currentChangeOrder && !filter(approvedChanges, { key: changeOrderKey }).length ? concat([this._currentChangeOrder], approvedChanges) : approvedChanges;
				const changeGraph = {};

				forEach(changesToApply, co => {
					forEach(targetSet.filter(item => item.changeOrderKey === co.key), item => {
						if (changeGraph[item.rootKey]) {
							changeGraph[item.rootKey].parent = item;
						}
						else {
							changeGraph[item.rootKey] = item;
						}
					});
				});

				forEach(targetSet.filter(item => !item.changeOrderKey), item => {
					if (changeGraph[item.rootKey]) {
						changeGraph[item.rootKey].parent = item;
					}
					else {
						changeGraph[item.rootKey] = item;
					}
				});

				if (!config.preventStrictFilter && this._strictListFilter) {
					filteredSet = new Proxy(targetSet.filter(item => item.changeOrderKey === changeOrderKey), handler);
				}
				else {
					const headKeys = map(values(changeGraph), x => x.key);
					filteredSet = new Proxy(targetSet.filter(item => headKeys.indexOf(item.key) !== -1), handler);
				}
			}
			else {
				const handler = new ContractSetHandler(assign({ targetSet: targetSet, currentChangeOrder: this._currentChangeOrder }, config));
				filteredSet = new Proxy(targetSet.filter(item => item.changeOrderKey === changeOrderKey), handler);
			}
		}

		this[`_${setPropertyName}`] = filteredSet;

		return filteredSet;
	}

	_getApprovedChanges(target) {
		this._approvedChanges = this._approvedChanges = orderBy(target.getApprovedChangeOrders(), ['currentStatusDate'], ['desc']);

		return this._approvedChanges;
	}
}
/* eslint-enable camelcase */

export default (jobCost, currentChangeOrder, strictListFilter = false) => {
	const clone = jobCostFactory(jobCost.toDocument());
	const changeOrder = currentChangeOrder ? clone.changeOrders[currentChangeOrder.key] || currentChangeOrder : undefined;
	const result = new Proxy(clone, new ContractHandler(changeOrder, strictListFilter));

	clone.contractProxy = result;

	return result;
};